import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/seo/SEO';
import {
  ArrowRight, Workflow, Radio, Database, BarChart3,
  Bot, Plug, Calendar,
} from 'lucide-react';
import BonacciStudioIcon from '../components/brand/BonacciStudioIcon';
import AuroraFlower from '../components/brand/AuroraFlower';

const MONO = { fontFamily: '"JetBrains Mono", ui-monospace, monospace' };
const GROTESK = { fontFamily: '"Space Grotesk", sans-serif' };

// Tiny scroll fade-up hook — IntersectionObserver, no library.
// Respects prefers-reduced-motion (CSS gates the transition).
const useScrollReveal = () => {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const targets = root.querySelectorAll('[data-reveal]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);
  return rootRef;
};

// Agent transcript — the "product surface" hero panel.
const AgentTerminal = () => (
  <div
    // bs-invert: a terminal reads as a terminal, so this panel stays dark in both
    // themes and keeps its light-on-dark text.
    className="bs-invert rounded-[13px] border border-white/[0.08] overflow-hidden shadow-[0_40px_90px_-45px_rgba(0,0,0,0.95)] backdrop-blur-md"
    style={{ background: 'linear-gradient(180deg, rgba(14,12,20,0.92), rgba(10,9,15,0.92))' }}
  >
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.08] bg-white/[0.015]">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f57' }} />
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#febc2e' }} />
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#28c840' }} />
      <span className="ml-2 text-xs text-white/30" style={MONO}>bonacci-agent · <b className="text-white/50 font-semibold">prod-postgres</b></span>
    </div>
    <div className="p-4 text-[12.5px] leading-[1.85]" style={MONO}>
      <div className="bs-term-reveal flex gap-2.5" style={{ animationDelay: '.05s' }}>
        <span className="text-violet-400 select-none">›</span>
        <span className="text-white">build a daily revenue rollup from orders</span>
      </div>
      <div className="bs-term-reveal my-2 rounded-lg border border-white/[0.05] border-l-2 border-l-cyan-400 px-3 py-2" style={{ animationDelay: '.5s', background: 'rgba(56,189,248,0.03)' }}>
        <div className="text-cyan-400 text-[11.5px]">⚙ inspect_schema(table: "orders")</div>
        <div className="text-white/55 text-[11.5px]">→ <span className="text-emerald-400">14 columns</span>, 3 foreign keys, ~2.4M rows</div>
      </div>
      <div className="bs-term-reveal my-2 rounded-lg border border-white/[0.05] border-l-2 border-l-cyan-400 px-3 py-2" style={{ animationDelay: '1s', background: 'rgba(56,189,248,0.03)' }}>
        <div className="text-cyan-400 text-[11.5px]">⚙ profile_column(col: "created_at")</div>
        <div className="text-white/55 text-[11.5px]">→ range 2023-01 … 2026-06, <span className="text-emerald-400">no nulls</span></div>
      </div>
      <div className="bs-term-reveal flex gap-2.5" style={{ animationDelay: '1.5s' }}>
        <span className="text-violet-400 select-none">›</span>
        <span className="text-white/55">writing transform…</span>
      </div>
      <div className="bs-term-reveal my-2 rounded-lg border border-white/[0.05] px-3 py-2.5 text-white/55" style={{ animationDelay: '1.9s', background: 'rgba(255,255,255,0.015)' }}>
        <span style={{ color: '#c792ea' }}>SELECT</span> date_trunc(<span style={{ color: '#ecc48d' }}>'day'</span>, created_at) <span style={{ color: '#c792ea' }}>AS</span> day,<br />
        &nbsp;&nbsp;<span style={{ color: '#82aaff' }}>sum</span>(amount) <span style={{ color: '#c792ea' }}>AS</span> revenue,<br />
        &nbsp;&nbsp;<span style={{ color: '#82aaff' }}>count</span>(<span style={{ color: '#f78c6c' }}>*</span>) <span style={{ color: '#c792ea' }}>AS</span> orders<br />
        <span style={{ color: '#c792ea' }}>FROM</span> orders <span style={{ color: '#c792ea' }}>GROUP BY</span> <span style={{ color: '#f78c6c' }}>1</span> <span style={{ color: '#c792ea' }}>ORDER BY</span> <span style={{ color: '#f78c6c' }}>1</span>
        <span className="bs-cursor inline-block w-[7px] h-[15px] align-[-2px] ml-0.5 bg-violet-400" />
      </div>
      <div className="bs-term-reveal flex gap-2.5" style={{ animationDelay: '2.3s' }}>
        <span className="text-emerald-400 select-none">✓</span>
        <span className="text-white">pipeline created · scheduled daily 02:00 UTC</span>
      </div>
    </div>
  </div>
);

const FEATURES = [
  { icon: Bot, title: 'Agentic AI Engine', desc: 'AI agents with real tool access — connect to databases, inspect schemas, write ETL, and execute pipelines autonomously.' },
  { icon: Workflow, title: 'Visual Pipeline Builder', desc: 'Design data pipelines with a drag-and-drop interface. No coding required.' },
  { icon: Radio, title: 'Real-time Streaming', desc: 'Process and stream data in real-time with native Apache Kafka integration.' },
  { icon: Database, title: 'Multi-Database Support', desc: 'Connect to PostgreSQL, MySQL, Oracle, SQL Server, Snowflake, and more.' },
  { icon: Plug, title: 'MCP & Multi-Model', desc: 'Extend agents with Model Context Protocol. Supports OpenAI, Anthropic, Gemini, Ollama.' },
  { icon: BarChart3, title: 'PySpark ML & Monitoring', desc: 'Run PySpark pipelines with auto-dependency install, real-time logs, and monitoring.' },
];

const STEPS = [
  { n: '01', title: 'Connect', desc: 'Link databases, SSH servers, and MCP tools in seconds.' },
  { n: '02', title: 'Ask the Agent', desc: 'Describe what you need in plain English — the agent explores and builds it.' },
  { n: '03', title: 'Build & Execute', desc: 'Generate PySpark, SQL, or visual pipelines — then run with one click.' },
  { n: '04', title: 'Monitor', desc: 'Track execution logs, performance, and data quality in real-time.' },
];

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const revealRef = useScrollReveal();

  useEffect(() => {
    if (isAuthenticated()) navigate('/home');
  }, [isAuthenticated, navigate]);

  return (
    <div ref={revealRef} className="bs-public min-h-screen w-full bg-[#060509] text-white bs-landing relative overflow-hidden" style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif' }}>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .bs-landing .bs-step-connector { background-size: 200% 100%; animation: bsDashFlow 3.6s linear infinite; }
          .bs-landing [data-reveal] { opacity: 0; transform: translateY(14px); transition: opacity 0.7s ease, transform 0.7s ease; }
          .bs-landing [data-reveal].is-revealed { opacity: 1; transform: translateY(0); }
          .bs-landing [data-reveal-delay="1"].is-revealed { transition-delay: 0.06s; }
          .bs-landing [data-reveal-delay="2"].is-revealed { transition-delay: 0.12s; }
          .bs-landing [data-reveal-delay="3"].is-revealed { transition-delay: 0.18s; }
          .bs-landing [data-reveal-delay="4"].is-revealed { transition-delay: 0.24s; }
          .bs-landing [data-reveal-delay="5"].is-revealed { transition-delay: 0.30s; }
          .bs-landing [data-reveal-delay="6"].is-revealed { transition-delay: 0.36s; }
          .bs-landing .bs-term-reveal { opacity: 0; transform: translateY(6px); animation: bsRise 0.5s ease forwards; }
          .bs-landing .bs-cursor { animation: bsBlink 1.1s steps(1) infinite; }
        }
        @keyframes bsDashFlow { 0% { background-position: 200% 0; } 100% { background-position: 0 0; } }
        @keyframes bsRise { to { opacity: 1; transform: none; } }
        @keyframes bsBlink { 50% { opacity: 0; } }
        .bs-landing .bs-step-connector {
          background-image: linear-gradient(90deg, rgba(167,139,250,0) 0%, rgba(167,139,250,0.5) 30%, rgba(167,139,250,0.5) 50%, rgba(167,139,250,0) 80%);
        }
      `}</style>
      <SEO
        title="Studio - Agentic AI data engineering platform"
        description="Build pipelines visually. Query data conversationally. Monitor everything autonomously."
        url="https://studio.thinkingdbx.com"
      />

      {/* Aurora Fibonacci-flower background */}
      <AuroraFlower focusX={0.5} />

      {/* Nav */}
      <nav className="border-b border-white/[0.08] relative z-20 bg-[#060509]/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="no-underline bs-text inline-flex items-center gap-2.5">
            <BonacciStudioIcon size={26} />
            <span className="text-base tracking-tight" style={{ ...GROTESK, fontWeight: 600 }}>
              Studio
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/pricing" className="px-3 py-2 text-sm text-white/60 hover:text-white transition-colors">Pricing</Link>
            <Link to="/auth" className="px-4 py-2 text-sm font-medium bg-white text-black rounded-md hover:bg-white/90 transition-colors inline-flex items-center gap-1.5">
              Sign In <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero — golden-ratio copy + agent terminal */}
      <section className="relative z-10" style={{ minHeight: 'calc(100vh - 65px)' }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-14 items-center" style={{ minHeight: 'calc(100vh - 65px)', paddingTop: '3.5rem', paddingBottom: '4.5rem' }}>
          {/* Left — copy */}
          <div>
            <span className="text-xs uppercase text-violet-300/90 mb-6 inline-block" style={{ ...MONO, letterSpacing: '0.28em' }}>
              By the golden ratio
            </span>
            <h1 className="font-semibold leading-[0.98] tracking-tight mb-6" style={{ ...GROTESK, fontSize: 'clamp(2.6rem, 5vw, 4.2rem)' }}>
              Data, in its<br />natural order.
              <span className="block mt-3 text-[0.6em] font-normal text-violet-200" style={MONO}>φ = 1.6180339887…</span>
            </h1>
            <p className="text-white/60 leading-relaxed mb-7 max-w-lg" style={{ fontSize: '1.06rem' }}>
              Studio grows pipelines the way nature grows, one connection at a time, each building on the last.
              Agentic ETL, streaming &amp; quality, from a single studio.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-[10px] font-semibold text-sm text-white transition-all
                           bg-gradient-to-r from-violet-700 to-violet-500 hover:brightness-110 shadow-[0_10px_30px_-12px_rgba(124,58,237,0.7)]"
              >
                Start free <ArrowRight size={16} />
              </Link>
              <a
                href="https://calendly.com/thinkingdbx/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-[10px] font-medium text-sm bs-text border border-white/15 hover:border-white/30 hover:bg-white/5 transition-colors"
              >
                <Calendar size={15} /> Book a demo
              </a>
            </div>
            <div className="mt-7 flex flex-wrap gap-5 text-xs text-white/35" style={MONO}>
              <span><b className="text-white/55 font-semibold">2 min</b> to first pipeline</span>
              <span><b className="text-white/55 font-semibold">6+</b> databases</span>
              <span><b className="text-white/55 font-semibold">4</b> runtimes</span>
            </div>
          </div>

          {/* Right — agent terminal */}
          <div className="w-full">
            <AgentTerminal />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-24 border-t border-white/[0.06] relative z-10">
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-center mb-14">
            <p className="text-[10px] font-semibold text-violet-300/80 uppercase tracking-[0.18em] mb-3">Features</p>
            <h2 className="text-3xl sm:text-4xl font-semibold mb-3 tracking-tight">Everything you need for data engineering</h2>
            <p className="text-white/50">Build, deploy, and monitor data pipelines — all from a single platform.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map(({ icon: Icon, title, desc }, idx) => (
              <div
                key={title}
                data-reveal
                data-reveal-delay={idx % 3}
                className="group bg-gradient-to-b from-white/[0.035] to-white/[0.01] hover:from-white/[0.06] hover:to-white/[0.02] border border-white/[0.06] hover:border-violet-500/30 rounded-xl p-6 transition-all backdrop-blur-sm"
              >
                <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-300 mb-4 group-hover:bg-violet-500/20 group-hover:border-violet-500/40 transition-colors">
                  <Icon size={17} />
                </div>
                <h3 className="text-[15px] font-semibold mb-1.5 tracking-tight">{title}</h3>
                <p className="text-[13px] text-white/55 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-24 border-t border-white/[0.06] relative z-10">
        <div className="max-w-5xl mx-auto relative">
          <div className="text-center mb-14">
            <p className="text-[10px] font-semibold text-violet-300/80 uppercase tracking-[0.18em] mb-3">Workflow</p>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Up and running in minutes</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {STEPS.map((s, i) => (
              <div key={s.n} data-reveal data-reveal-delay={i} className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-mono text-violet-300/70 bg-violet-500/10 border border-violet-500/15 px-2 py-0.5 rounded">
                    {s.n}
                  </span>
                  {i < STEPS.length - 1 && (
                    <span className="bs-step-connector hidden lg:block flex-1 h-px" />
                  )}
                </div>
                <h3 className="text-base font-semibold mb-1.5 tracking-tight">{s.title}</h3>
                <p className="text-[13px] text-white/55 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 py-24 border-t border-white/[0.06] relative z-10 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.10)_0%,transparent_70%)] pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative">
          <div className="inline-flex mb-6">
            <BonacciStudioIcon size={56} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight">Ready to ship your data?</h2>
          <p className="text-white/55 mb-8 max-w-lg mx-auto">
            Start free. Connect your first database in under a minute, then let the agent take over.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/auth"
              className="px-6 py-3 bg-gradient-to-r from-violet-700 to-violet-500 hover:brightness-110 text-white text-sm font-semibold rounded-md transition-all inline-flex items-center gap-2 shadow-[0_10px_30px_-12px_rgba(124,58,237,0.7)]"
            >
              Start Free <ArrowRight size={16} />
            </Link>
            <a
              href="https://calendly.com/thinkingdbx/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 border border-white/15 text-sm font-medium rounded-md hover:border-white/40 hover:bg-white/5 transition-colors inline-flex items-center gap-2"
            >
              <Calendar size={16} /> Schedule Demo
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-white/[0.08] relative z-10">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2.5 text-white/40">
            <BonacciStudioIcon size={20} spiral={false} />
            <span className="text-sm" style={{ ...GROTESK, fontWeight: 600 }}>
              Studio
            </span>
          </div>
          <div className="flex gap-5 text-sm text-white/40">
            <a href="/legal?doc=terms" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Terms</a>
            <a href="/legal?doc=privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Privacy</a>
            <a href="/legal?doc=refund" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors">Refund</a>
            <Link to="/pricing" className="hover:text-white/70 transition-colors">Pricing</Link>
          </div>
          <div className="text-xs text-white/30 text-center sm:text-right">
            <div>&copy; 2026 ThinkingDBx Pvt. Ltd.</div>
            <div>D&amp;B D-U-N-S Number: 771797736</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
